import { IAuthenticationProvider, IdentityType } from '../backend/interfaces/IAuthenticationProvider';
import { GetPrimaryIdentityInfo } from '../backend/office-js/auth/IdentityUtil';
import { OnedriveBaseUrlFromAuthEnvironment } from '../backend/oneDrive/OnedriveTokenProvider';
import { IS_DEV_BUILD } from '../AppConstants';
import { LogEvents } from './LogEvents';
import { ServiceWorkerAudioProxyKeys } from './telemetry/ServiceWorkerAudioProxyKeys';
import { ErrorStringFromException } from './utils';

const SW_CONTROLLER_TIMEOUT_MS = 3000;
const SW_AUDIO_PROXY_BASE = '_sw/audio';

export function RegisterServiceWorker(): void {
    if ('serviceWorker' in navigator) {
        navigator.serviceWorker.register('sw.js').catch(e =>
            console.warn('[SW] Service Worker registration failed:', e)
        );
    }
}

async function WaitForServiceWorkerController(): Promise<void> {
    if (!('serviceWorker' in navigator)) return;
    await Promise.race([
        navigator.serviceWorker.ready.then(() => {
            if (!navigator.serviceWorker.controller) {
                return new Promise<void>(resolve => {
                    navigator.serviceWorker.addEventListener('controllerchange', () => resolve(), { once: true });
                });
            }
            return Promise.resolve();
        }),
        new Promise<void>(resolve => setTimeout(resolve, SW_CONTROLLER_TIMEOUT_MS))
    ]);
}

// Returns the proxy audioUrl if SW is available, null otherwise.
export async function GetServiceWorkerAudioUrl(
    authProvider: IAuthenticationProvider,
    driveId: string,
    itemId: string,
    spoDownloadUrl: string
): Promise<string | null> {
    await WaitForServiceWorkerController();

    if (!('serviceWorker' in navigator) || !navigator.serviceWorker.controller) {
        LogEvents.ServiceWorkerAudioProxy.AddData([
            { key: ServiceWorkerAudioProxyKeys.Succeeded, value: false },
            { key: ServiceWorkerAudioProxyKeys.FailureReason, value: 'NoController' },
        ]);
        return null;
    }

    try {
        let tokenResource: string;
        let identityType: IdentityType;
        if (IS_DEV_BUILD) {
            // GetPrimaryIdentityInfo is not available in local dev; default to ODB
            tokenResource = new URL(spoDownloadUrl).origin;
            identityType = IdentityType.ODB;
        } else {
            const identityInfo = await GetPrimaryIdentityInfo();
            identityType = identityInfo.type;
            tokenResource = identityType === IdentityType.ODB
                ? new URL(spoDownloadUrl).origin
                : OnedriveBaseUrlFromAuthEnvironment(authProvider.GetAuthEnvironment(), identityType);
        }
        const spoToken = await authProvider.GetAccessToken({ resource: tokenResource });

        const strippedUrl = new URL(spoDownloadUrl);
        strippedUrl.searchParams.delete('tempauth');

        navigator.serviceWorker.controller.postMessage({
            type: 'SET_AUDIO_SOURCE',
            driveId,
            itemId,
            spoToken,
            downloadUrl: strippedUrl.toString(),
        });

        LogEvents.ServiceWorkerAudioProxy.AddData([
            { key: ServiceWorkerAudioProxyKeys.Succeeded, value: true },
            { key: ServiceWorkerAudioProxyKeys.IdentityType, value: identityType },
        ]);
        return `${SW_AUDIO_PROXY_BASE}/${encodeURIComponent(driveId)}/${encodeURIComponent(itemId)}`;
    } catch (e) {
        console.warn('[SW] GetServiceWorkerAudioUrl failed:', e);
        LogEvents.ServiceWorkerAudioProxy.AddData([
            { key: ServiceWorkerAudioProxyKeys.Succeeded, value: false },
            { key: ServiceWorkerAudioProxyKeys.IdentityType, value: 'unknown' },
            { key: ServiceWorkerAudioProxyKeys.FailureReason, value: ErrorStringFromException(e) },
        ]);
        return null;
    }
}
